import type { VenueBlock, VenueLevel } from '../types';

/**
 * Floor plan for Nocturne Con, ported from the prototype's `makeLevels()`.
 *
 * A real tenant authors these blocks in the CMS venue editor and the bundle
 * arrives with `venue.levels` already populated — this generator only runs when
 * the bundle ships an empty levels array, which is how the sample data works.
 */

type NamedStand = { n: string; k: string; s: string };

function grid(
  out: VenueBlock[],
  zone: string,
  x0: number, z0: number,
  cols: number, rows: number,
  w: number, d: number,
  gapX: number, gapZ: number,
  names: NamedStand[],
) {
  let n = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const nm = names[n % names.length];
      out.push({
        id: `${zone}-${r}${c}`,
        zone,
        name: nm.n,
        kind: nm.k,
        note: nm.s,
        x: x0 + c * (w + gapX),
        z: z0 + r * (d + gapZ),
        w, d, h: 0.5,
      });
      n++;
    }
  }
}

const artistNames: NamedStand[] = [
  { n: 'Inkwell & Iron', k: 'Original art', s: 'Prints, sketch commissions on site' },
  { n: 'Saltmarsh Studio', k: 'Illustration', s: 'Con-exclusive foil prints (200 run)' },
  { n: 'Hollow Bell Art', k: 'Manga art', s: 'Signing 2–4pm Fri & Sat' },
  { n: 'Nine Lanterns', k: 'Watercolor', s: 'Live painting all weekend' },
  { n: 'Pale Fox Press', k: 'Zines', s: 'Risograph minicomics, $6–14' },
  { n: 'Bright Vellum', k: 'Portraits', s: 'Same-day character portraits' },
];
const comicNames: NamedStand[] = [
  { n: 'Third Panel Comics', k: 'Back issues', s: 'Bronze & copper age, graded wall' },
  { n: 'Meridian Books', k: 'Graphic novels', s: 'Publisher signings hourly' },
  { n: 'Vault 12', k: 'Key issues', s: 'CGC slabs, buy/sell/trade' },
  { n: 'Fold & Crease', k: 'Supplies', s: 'Bags, boards, boxes' },
];
const gamingNames: NamedStand[] = [
  { n: 'Dice Tavern', k: 'Tabletop', s: 'Open tables, learn-to-play every 30min' },
  { n: 'Cartridge Row', k: 'Retro games', s: 'Imports, repairs, CRT stations' },
  { n: 'Ashen Reach Booth', k: 'Video game', s: 'Playable demo + dev Q&A' },
  { n: 'Sixth Sense TCG', k: 'Card games', s: 'Singles, sealed, side events' },
];
const craftNames: NamedStand[] = [
  { n: 'Forge & Foam', k: 'Cosplay armor', s: 'Repair bar — free foam patching' },
  { n: 'Thistledown', k: 'Fiber crafts', s: 'Knit patterns, plush kits' },
  { n: 'Brass Owl Props', k: 'Props', s: 'Screen-accurate replica builds' },
  { n: 'Wick & Wax', k: 'Candles', s: 'Fandom-scent sets, exclusives' },
];

export function defaultLevels(): VenueLevel[] {
  const l1: VenueBlock[] = [];
  grid(l1, 'artist', -7.5, -5.5, 6, 2, 1.5, 1.4, 0.5, 1.6, artistNames);
  grid(l1, 'comics', -7.5, 1.2, 4, 2, 2.1, 1.4, 0.5, 1.6, comicNames);
  grid(l1, 'gaming', 3.2, -5.5, 3, 2, 2.4, 1.9, 0.6, 1.6, gamingNames);
  grid(l1, 'crafts', 3.2, 1.2, 3, 2, 2.4, 1.4, 0.6, 1.6, craftNames);

  l1.push({ id: 'photo-a', zone: 'photo', name: 'Photo Op Studio A', kind: 'Photo ops', note: 'Dara Vance · Sat 11:20', x: -7.5, z: 6.4, w: 4.6, d: 2.4, h: 1.1 });
  l1.push({ id: 'photo-b', zone: 'photo', name: 'Photo Op Studio B', kind: 'Photo ops', note: 'Duo & group sets', x: -2.2, z: 6.4, w: 3.4, d: 2.4, h: 1.1 });
  l1.push({ id: 'autos', zone: 'autos', name: 'Autograph Row', kind: 'Autographs', note: '14 tables · queue board at entry', x: 2.0, z: 6.4, w: 8.2, d: 2.4, h: 0.9 });
  l1.push({ id: 'stage', zone: 'stage', name: 'Main Stage', kind: 'Programming', note: '2,400 seats · Hall D', x: -1.0, z: -9.6, w: 9.0, d: 3.0, h: 1.8 });

  const rooms = [
    { n: 'Panel Room 101', s: 'Capacity 300 · Starfall track' },
    { n: 'Panel Room 102', s: 'Capacity 300 · Anime track' },
    { n: 'Panel Room 103', s: 'Capacity 180 · Comics craft' },
    { n: 'Panel Room 104', s: 'Capacity 180 · Tabletop' },
    { n: 'Workshop 201', s: 'Capacity 60 · hands-on, ticketed' },
    { n: 'Workshop 202', s: 'Capacity 60 · hands-on, ticketed' },
  ];
  const l2: VenueBlock[] = rooms.map((r, i) => ({
    id: `room-${i}`, zone: 'panels', name: r.n, kind: 'Panel room', note: r.s,
    x: -8 + (i % 3) * 5.6, z: i < 3 ? -5 : 1.2, w: 4.6, d: 4.0, h: 1.2,
  }));
  l2.push({ id: 'ballroom', zone: 'stage', name: 'Grand Ballroom', kind: 'Special events', note: 'Second Breakfast · Cosplay Runway', x: -6.0, z: 6.6, w: 9.4, d: 3.4, h: 1.5 });
  l2.push({ id: 'lounge', zone: 'crafts', name: 'Quiet Lounge', kind: 'Amenity', note: 'Low light, low noise, all hours', x: 4.6, z: 6.6, w: 4.4, d: 3.4, h: 1.0 });

  const plate = { w: 25, d: 23, x: 1, z: -0.4 };
  return [
    { id: '1', label: 'Level 1 · Hall', blocks: l1, plate },
    { id: '2', label: 'Level 2 · Panels', blocks: l2, plate },
  ];
}
